import { func, object, string } from 'prop-types';
import { injectIntl } from 'react-intl';
import React from 'react';
import { path, pick } from 'ramda';

import { Col, Row } from 'react-flexbox-grid';

import QuestionBlock from './QuestionBlock';
import ControlSteps from '../../OnlineRehabilitation/components/Contents/components/ControlSteps';
import TextFieldArea from 'components/TextFieldArea';
import ActionButton from 'components/ActionButton';
import RehabHeader from './RehabHeader';
import {
  DiaryWeeklyRecapChart,
  weekSummaryTableContent,
} from '../../OnlineRehabilitation/components/Contents/Rehab3/Charts';
import SummaryTable from '../../OnlineRehabilitation/components/Contents/components/SummaryTable';
import ContentColumns from '../../OnlineRehabilitation/components/Contents/components/ContentColumns';

const TextInputArea = ({ value, onChange, onSave }) => (
  <Row>
    <Col xs={7}>
      <TextFieldArea editing input={{ value, onChange }} rows={4} style={{ width: '100%' }} />
    </Col>
    <Col xs={5}>
      <Row style={{ marginLeft: '4rem', marginTop: '0.8rem' }}>
        <Col>{onSave && <ActionButton localeId="npView.history.save" onClick={onSave} secondary border />}</Col>
      </Row>
    </Col>
  </Row>
);

TextInputArea.propTypes = {
  value: string,
  onChange: func,
  onSave: func,
};

const Rehab3 = ({ fm, data, dataFinished, fatigueData, commitPutRehab, intl: { formatMessage } }) => {
  const controlStepsData = {
    currentStatus: path(['currentStatus'], data),
    clearlyWorseStatus: path(['clearlyWorseStatus'], data),
    nearFutureTarget: path(['nearFutureTarget'], data),
    betterStatus: path(['betterStatus'], data),
    clearlyBetterStatus: path(['clearlyBetterStatus'], data),
  };

  const [text, setText] = React.useState(null);

  const savedFeedbackText = path(['neurologistsComments'], dataFinished);
  const feedbackValue = text || savedFeedbackText || '';

  const onChangeText = event => {
    const eventValue = path(['currentTarget', 'value'], event);
    setText(eventValue);
  };

  const onSave =
    savedFeedbackText !== feedbackValue
      ? () => {
          commitPutRehab({ rehab3: { ...data, neurologistsComments: text } });
          setText(null);
        }
      : null;

  const includedDayKeys = ['0', '1', '2', '3', '4', '5', '6'];
  const diaryDays = pick(includedDayKeys, fatigueData);
  const days = Object.values(diaryDays);

  const fmString = id => formatMessage({ id });

  const weekSummaryTableContentArray = weekSummaryTableContent(days, fmString);
  return (
    <React.Fragment>
      <RehabHeader fm={fm} rehab={3} date={data.finished} />

      <Row>
        <ContentColumns
          firstColumn={
            <div style={{ marginTop: '2rem' }}>
              {path(['startDate'], fatigueData) && <DiaryWeeklyRecapChart diary={fatigueData} />}
            </div>
          }
          secondColumn={
            <div style={{ paddingBottom: 20 }}>
              <SummaryTable content={weekSummaryTableContentArray} />
            </div>
          }
          alignEnd
        />
      </Row>

      <h3>{fm('onlineRehabilitation.rehab3.1.recapDescription')}</h3>
      <QuestionBlock
        question={fm('onlineRehabilitation.rehab3.1.headerStrongFatigue')}
        answer={data.strongFatiqueFactors}
      />
      <QuestionBlock
        question={fm('onlineRehabilitation.rehab3.1.headerMildFatigue')}
        answer={data.mildFatiqueFactors}
      />

      <QuestionBlock question={fm('npView.rehab3.steps')} answer={<ControlSteps steps={controlStepsData} fm={fm} />} />
      <QuestionBlock
        question={fm('onlineRehabilitation.rehab3.8.header')}
        answer={data.plannedChangesToManageFatigue}
      />
      <QuestionBlock
        question={fm('npView.rehab3.feedback')}
        answer={<TextInputArea value={feedbackValue} onChange={onChangeText} onSave={onSave} />}
      />
    </React.Fragment>
  );
};

Rehab3.propTypes = {
  fm: func,
  data: object,
  dataFinished: object,
  fatigueData: object,
  commitPutRehab: func,
  intl: object,
};
export default injectIntl(Rehab3);
